import { createSelector } from "@ngrx/store";

import { QuoteStatus } from "src/api-service";
import { selectIsSubscriptionQuote, selectQuoteState } from "../../../../pages/quote/quote.selectors";
import { selectVersion } from "../../../../pages/quote/version/version.selectors";
import { selectSelectedCompany } from "../../../../user/user.selectors";
import {
    CreditCheckOutcome,
    decideCreditCheck,
    isCreditCheckBlocking,
    isDenialOverridden,
    isDomesticCustomer,
    wouldRerunHitProvider,
} from "./decide-credit-check";

// **************************************************************************
// QUOTE CREDIT CHECK SELECTORS
//
// These selectors derive credit check verdicts from the quote's stored
// credit check data. The actual credit check result is stored on the
// Quote object, not in the credit-check store.
// **************************************************************************

/** Stored credit-check facts for the current quote, or null. */
export const selectQuoteCreditCheck = createSelector(
    selectQuoteState,
    state => state.creditCheck ?? null,
);

/** Whether the chosen payment term requires a credit check (i.e. isn't 100% advance payment). */
export const selectRequiresCreditCheck = createSelector(
    selectQuoteState,
    state => state.deliveryInfo?.paymentTerm?.requiresCreditCheck === true,
);

/**
 * Whether the real credit-check flow is available for this quote: the selling company's country
 * has a configured provider AND the customer is domestic (billed in that same country). Lookups on
 * foreign companies are billed per check, so those quotes fall back to the manual attestation, the
 * same as companies without a provider.
 *
 * The step, the billing-tab hint and the manual toggle in the order/review dialogs all read this,
 * so they switch together.
 */
export const selectCreditCheckEnabled = createSelector(
    selectSelectedCompany,
    selectQuoteState,
    (company, quote) => company?.creditCheckEnabled === true
        && isDomesticCustomer(quote.customer?.billingAddress?.countryCode, company?.countryCode),
);

/**
 * Order total the check is judged against. Read from the SELECTED version, which every item and
 * version mutation refreshes — so this is current at the moment the user clicks send/order.
 */
export const selectQuoteTotalIncVat = createSelector(
    selectVersion,
    version => version?.summary?.totalSalesPriceIncVat ?? 0,
);

/**
 * The effective verdict right now. Re-derived rather than read from a stored status, because the
 * order total moves independently of the stored record.
 */
export const selectCreditCheckOutcome = createSelector(
    selectQuoteCreditCheck,
    selectQuoteState,
    selectQuoteTotalIncVat,
    (check, quote, total) => decideCreditCheck(
        check,
        quote.customer?.code,
        quote.customer?.registrationNo,
        total,
    ),
);

/** Whether the current verdict is a denial that a super user has overridden. */
export const selectIsCreditCheckOverridden = createSelector(
    selectCreditCheckOutcome,
    selectQuoteCreditCheck,
    (outcome, check) => isDenialOverridden(outcome, check),
);

/** Quote statuses past the point where a credit-check override could still matter. */
const FINISHED_QUOTE_STATUSES: ReadonlySet<QuoteStatus> = new Set([
    QuoteStatus.Approved,
    QuoteStatus.Lost,
    QuoteStatus.Removed,
    QuoteStatus.Replaced,
    QuoteStatus.Archived,
    QuoteStatus.AutoArchived,
]);

/**
 * Whether the current user may override the credit check from outside the send / create-order
 * flow: on a denial not yet overridden, on a quote that isn't finished.
 *
 * Deliberately not gated on the quote lock: a sent quote is locked, and that is exactly when the
 * override is needed before creating the order. The override is an audit stamp on the check, not
 * an edit of the quote.
 */
export const selectCanOverrideCreditCheck = createSelector(
    selectIsSubscriptionQuote,
    selectQuoteState,
    selectCreditCheckOutcome,
    selectIsCreditCheckOverridden,
    (isSubscription, quote, outcome, overridden) =>
        !isSubscription
        && !FINISHED_QUOTE_STATUSES.has(quote.status)
        && outcome === CreditCheckOutcome.Denied
        && !overridden,
);

/**
 * True when the credit-check step should be shown before sending or ordering.
 *
 * Subscription quotes are excluded: they are credit-checked at creation with a different formula,
 * and that result is stored against the customer rather than the quote — so the outcome here would
 * read as NoCheck and prompt for a second, pointless check. Mirrors `CreditCheckGate.AppliesTo` on
 * the server, which refuses to enforce anything on them for the same reason.
 *
 * A denial that has already been overridden is skipped — asking the user to override again in the
 * order flow would feel stuck without explanation.
 */
export const selectNeedsCreditCheckStep = createSelector(
    selectIsSubscriptionQuote,
    selectCreditCheckEnabled,
    selectRequiresCreditCheck,
    selectCreditCheckOutcome,
    selectQuoteCreditCheck,
    (isSubscription, enabled, requires, outcome, check) =>
        !isSubscription && enabled && requires && outcome !== CreditCheckOutcome.Approved
        && (outcome !== CreditCheckOutcome.Denied || isCreditCheckBlocking(outcome, check)),
);

/** Whether re-running would reach the provider, or just return the same cached score. */
export const selectRerunWouldHitProvider = createSelector(
    selectQuoteCreditCheck,
    check => wouldRerunHitProvider(check),
);
